import PropTypes from 'prop-types';
import { useMemo, useEffect } from 'react';
// material
import { CssBaseline } from '@mui/material';
import { ThemeProvider as MUIThemeProvider, createTheme, StyledEngineProvider, alpha } from '@mui/material/styles';
//
import palette from './palette';
import typography from './typography';
import componentsOverride from './overrides';
import shadows, { customShadows } from './shadows';
import { THEME_PRESETS, DEFAULT_THEME_COLOR } from './presets';
import { useSettings } from '../context/SettingsContext';

// ----------------------------------------------------------------------

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default function ThemeProvider({ children }) {
  const { themeColor } = useSettings();
  const preset = THEME_PRESETS[themeColor] || THEME_PRESETS[DEFAULT_THEME_COLOR];

  // Expose the active primary color to plain CSS (App.css) via CSS variables.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary-lighter', preset.lighter);
    root.style.setProperty('--color-primary-light', preset.light);
    root.style.setProperty('--color-primary-main', preset.main);
    root.style.setProperty('--color-primary-dark', preset.dark);
    root.style.setProperty('--color-primary-darker', preset.darker);
    root.style.setProperty('--color-primary-main-rgb', preset.mainRgb);
  }, [preset]);

  const themeOptions = useMemo(() => {
    const { mainRgb, ...primary } = preset;
    return {
      palette: {
        ...palette,
        primary: { ...primary },
        gradients: {
          ...palette.gradients,
          primary: `linear-gradient(135deg, ${preset.light}, ${preset.main})`,
        },
        action: {
          ...palette.action,
          selected: alpha(preset.main, 0.08),
        },
      },
      shape: { borderRadius: 8 },
      typography,
      shadows,
      customShadows,
    };
  }, [preset]);

  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  );
}
